import { memo, useCallback, useEffect, useState } from 'react';
import { cn, useMedias } from '@divlab/divanui';

import useTranslation from '@Queries/useTranslation';
import * as ModalWindows from '@Stores/ModalWindows';
import * as Profile from '@Queries/Profile';
import Button from '@UI/Button';
import Input from '@UI/Input';
import FormItem from '@UI/FormItem';
import Form from '@UI/Form';
import InputPhone from '@Components/InputPhone';
import Link from '@Navigation/Link';
import { useDeps } from '@Contexts/DI';
import Img from '@UI/Img';
import validationSchema from './schema';
import styles from './QuestionsBlock.module.css';

import type { ChangeEvent, FC, HTMLAttributes } from 'react';
import type { IFeedbackForm } from '@Pages/PageShowroom/typings';
import type { ServerErrorsOpts } from '@UI/Form';

export interface QuestionsBlockProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  feedbackForm?: IFeedbackForm;
}

const QuestionsBlock: FC<QuestionsBlockProps> = (props) => {
  const { feedbackForm, ...restProps } = props;
  const { data: profile } = Profile.useProfile();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorsOpts[]>([]);
  const { t } = useTranslation();
  const [profileId, setProfileId] = useState<number>();
  const { analytics } = useDeps();
  const [personalData, setPersonalData] = useState({
    name: '',
    phone: '',
  });
  const [successForm, setSuccessForm] = useState(false);

  const handleChangePersonalData = useCallback(
    (e: ChangeEvent<HTMLInputElement>, fieldName: 'name' | 'phone') => {
      setPersonalData((prev) => ({ ...prev, [fieldName]: e.target.value }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    setLoading(true);
  }, []);

  const handleError = useCallback(() => {
    setLoading(false);

    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  }, []);

  const handleResponse = useCallback(
    (response, data) => {
      setLoading(false);
      if (response.status === 'ok') {
        analytics.dispatchEvent('forms', {
          formName: 'feedback.callback',
          data: {
            action: 'send',
            source: 'showroom',
            inputValues: data,
          },
        });

        setSuccessForm(true);
      } else if (response.status === 'error') {
        if (response.code === 0) {
          setServerErrors(response.logs);
        } else {
          handleError();
        }
      } else {
        handleError();
      }
    },
    [analytics, handleError],
  );

  useEffect(() => {
    if (!profile) return;
    setProfileId((prev) => prev || profile.mindboxId);
    setPersonalData((prev) => ({
      name: prev.name || profile.firstName,
      phone: prev.phone || profile.phone,
    }));
  }, [profile]);

  const { isMobile } = useMedias();
  const imageSrc = isMobile ? feedbackForm.images.src_360 : feedbackForm.images.src;

  return (
    <div {...restProps} className={styles.questions}>
      <Img className={styles.image} src={imageSrc} alt='' />
      <div className={styles.contentBlock}>
        <div className={styles.textBlock}>
          {successForm ? (
            <div>
              <h3 className={styles.title}>{feedbackForm.doneTitle}</h3>
              <p className={styles.text}>{feedbackForm.doneText}</p>
            </div>
          ) : (
            <>
              <h3 className={styles.title}>{feedbackForm.title}</h3>
              <p className={styles.text}>{feedbackForm.text}</p>
            </>
          )}
        </div>
        {!successForm && (
          <Form
            {...restProps}
            className={cn(styles.contactsForm)}
            action='/backend/site/send-callback'
            name='feedback-call'
            validationSchemaBuilder={validationSchema}
            serverErrors={serverErrors}
            onSubmit={handleSubmit}
            onResponse={handleResponse}
            onCriticalError={handleError}
          >
            <div className={styles.formWrapper}>
              <FormItem className={styles.formItem}>
                <Input
                  whiteBordered
                  cnControl={styles.input}
                  type='text'
                  placeholder={feedbackForm.placeholders.name}
                  name='CallbackForm[name]'
                  value={personalData.name}
                  onChange={(e) => handleChangePersonalData(e, 'name')}
                />
              </FormItem>

              <FormItem className={styles.formItem}>
                <InputPhone
                  whiteBordered
                  key={profileId}
                  cnControl={styles.input}
                  type='text'
                  placeholder={feedbackForm.placeholders.phone}
                  name='CallbackForm[phone]'
                  value={personalData.phone}
                  onChange={(e) => handleChangePersonalData(e, 'phone')}
                />
              </FormItem>
            </div>

            <FormItem>
              <div className={styles.actions}>
                <Button className={styles.action} wide type='submit' waiting={loading}>
                  {feedbackForm.button.text}
                </Button>
              </div>
            </FormItem>

            <div className={styles.info}>
              {t('privacy-policy.before')}
              <Link to='/static-page/privacy-policy' view='white' target='_blank' underlined>
                {t('privacy-policy.link')}
              </Link>
              {t('privacy-policy.after')}
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default memo(QuestionsBlock);
